import { useMutation, useQuery } from '@tanstack/react-query';

import { apiRequest } from '@/lib/api-client';

import type { PreviewContent, PublishedContent } from './types';

/**
 * The marketing site's only CMS surface (`docs/standards/code-organization.md`).
 *
 * **Called with no session, and only ever the delivery half.** `/api/cms/content/...` is public,
 * serves the published snapshot, and has no route that can reach a working copy — so a bug here
 * cannot put a draft on the website. The management half (`/api/cms/manage/...`) belongs to the
 * admin console and is deliberately not reachable from this file.
 *
 * **`usePreview` is the one exception** (`CMS-012`), and it is bounded rather than trusted: it
 * calls `/api/cms/preview/{token}`, it is reachable from exactly one route (`/preview/:token`),
 * and what it returns is a `PreviewContent` — a type the ordinary page components cannot accept.
 * So the draft it fetches has one place it can be rendered, and that place draws the banner.
 */

const entryKey = (typeKey: string, slug: string) => ['cms', 'entry', typeKey, slug] as const;
const collectionKey = (typeKey: string) => ['cms', 'collection', typeKey] as const;

/** Thrown by `apiRequest` when a slug resolves to nothing. Not an error worth a console. */
export function isNotFound(error: unknown): boolean {
  if (error === null || typeof error !== 'object') return false;
  const status = (error as { status?: unknown }).status;
  return status === 404;
}

/**
 * One published page by slug (`CMS-009`).
 *
 * `retry: false` matters: a slug with no page is the *normal* outcome on this route — it is how
 * the site learns an address is not a CMS page — and retrying a 404 three times makes the
 * fallback to the site's own 404 take a visible moment.
 */
export function usePublishedPage(slug: string | undefined, options: { enabled?: boolean } = {}) {
  return usePublishedEntry('marketingPage', slug, options);
}

/**
 * One published entry of any type, by slug — a marketing page (`CMS-009`) or a case study's own
 * page (`CMS-015` AC-2).
 *
 * `retry: false` matters: a slug with no entry is the *normal* outcome on this route — it is how
 * the site learns an address is not CMS content — and retrying a 404 three times makes the
 * fallback to the site's own 404 take a visible moment.
 */
export function usePublishedEntry(
  typeKey: string,
  slug: string | undefined,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: entryKey(typeKey, slug ?? ''),
    enabled: slug !== undefined && slug !== '' && options.enabled !== false,
    retry: false,
    queryFn: () =>
      apiRequest<PublishedContent>(
        `/api/cms/content/${encodeURIComponent(typeKey)}/${encodeURIComponent(slug ?? '')}`,
      ),
  });
}

/**
 * A published collection — testimonials, case studies — **in the order the type declares**
 * (`CMS-015` AC-3).
 *
 * No sort option and no sort parameter, deliberately. The type declares `defaultSortField`, the
 * delivery route applies it, and every caller gets the same order: a collection that two pages
 * render in two different orders is a bug nobody reports.
 */
export function usePublishedCollection(typeKey: string) {
  return useQuery({
    queryKey: collectionKey(typeKey),
    retry: false,
    queryFn: () =>
      apiRequest<{ items: PublishedContent[] }>(`/api/cms/content/${encodeURIComponent(typeKey)}`),
    select: (response) => response.items,
  });
}

/**
 * Site-wide settings: navigation, footer, social links, contact details (`CMS-014`).
 *
 * **Read through the collection route rather than by slug.** `siteSettings` is a single-entry
 * type, so its collection is either empty or one item, and asking that way means the site does not
 * have to know what slug the one entry happens to carry. Asking for `/siteSettings/default` worked
 * only for as long as the seed and the site agreed on the word "default", which is a coupling with
 * no reason to exist — the editor does not even show a slug field for a single-entry type, so
 * nobody could have fixed a mismatch from the console.
 *
 * An unpublished or missing settings entry resolves to `undefined`, and every reader in
 * `constants.ts` turns that into the site's defaults (`CMS-014` AC-3).
 */
export function useSiteSettings() {
  return useQuery({
    queryKey: collectionKey('siteSettings'),
    retry: false,
    queryFn: () => apiRequest<{ items: PublishedContent[] }>('/api/cms/content/siteSettings'),
    select: (response) => response.items[0],
  });
}

/**
 * **Send a form** (`CMS-016` AC-1…AC-4) — the marketing site's only write.
 *
 * `POST /api/cms/forms/:slug/submissions`, which is on the *delivery* half like everything else in
 * this file: it reads a published form and writes a type the public cannot read back, so the
 * management API is still unreachable from the marketing site. The file header's claim survives
 * this addition and is worth re-reading rather than assuming.
 *
 * **No `retry`.** A submission is not idempotent — a retried send is a second message in somebody's
 * inbox — and the API's rate limit would refuse the retry anyway, turning a transient network
 * failure into a refusal the visitor cannot act on. React Query does not retry mutations by
 * default; this comment is here because "add a retry" is the obvious thing somebody would do to
 * this function and it is wrong.
 *
 * **`body` is the object, never `JSON.stringify(object)`.** `apiRequest` serialises what it is
 * given; a pre-serialised body goes out as JSON *inside a JSON string*, and `express.json()` is
 * strict about a top-level string, so the request is refused with a 400 before any route sees
 * it. That is exactly what this call site did, and every send from `/contact` failed on it —
 * the eleventh instance of the mistake `packages/ui/src/lib/api-client.ts` predicted. The type
 * refuses it now; the note stays because it explains the shape of the 400 to whoever meets it.
 */
export function useSubmitForm(slug: string) {
  return useMutation({
    // `readonly string[]` is a `checkboxGroup`'s answer — the chosen values, which the API checks
    // against the form's own options and stores joined in the form's order.
    mutationFn: (answers: Record<string, string | number | boolean | ReadonlyArray<string>>) =>
      apiRequest<{ successMessage: string }>(
        `/api/cms/forms/${encodeURIComponent(slug)}/submissions`,
        { method: 'POST', body: { answers } },
      ),
  });
}


const previewKey = (token: string) => ['cms', 'preview', token] as const;

/**
 * One draft, by preview token (`CMS-012` AC-1).
 *
 * `retry: false` and `gcTime: 0`. The retry for the reason the published lookups give — an
 * expired or wrong token is a *normal* outcome here and 404 is the answer, not a transient
 * failure worth three attempts. The `gcTime` is `CMS-012` AC-3 carried into the client: the
 * response says `no-store`, and leaving the draft sitting in the query cache after the component
 * unmounts would be this application keeping exactly what the header asked nothing to keep.
 */
export function usePreview(token: string | undefined) {
  return useQuery({
    queryKey: previewKey(token ?? ''),
    enabled: token !== undefined && token !== '',
    retry: false,
    gcTime: 0,
    queryFn: () => apiRequest<PreviewContent>(`/api/cms/preview/${encodeURIComponent(token ?? '')}`),
  });
}
